/**
 * Ko'rsatkich izohi (2026-10-24, foydalanuvchi so'rovi): yorliq yonidagi kichik "i" belgisi, ustiga borilganda (sichqoncha) yoki
 * bosilganda (telefon) ko'rsatkich nimani anglatishi qisqa chiqadi. Brauzerning `title` maslahati emas (qoida: brauzer elementlari
 * yo'q) -- Radix Popover, portal'da (karta `overflow`i kesmaydi). Havola yoki tugma ichida turishi mumkin (KPI kartasi, e'tibor
 * qatori): belgini bosish havolani ochmaydi. Matnlar `metrics` namespace'ida -- bitta ta'rif hamma joyda bir xil.
 */
import * as Popover from "@radix-ui/react-popover";
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { cn } from "@/lib/utils";

const OPEN_DELAY = 120;
const CLOSE_DELAY = 120;

export function InfoTip({ text, className, size = 13 }: { text: ReactNode; className?: string; size?: number }) {
  const { t } = useTranslation("metrics");
  const [open, setOpen] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const later = (next: boolean, ms: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(next), ms);
  };
  const hoverIn = (e: PointerEvent) => e.pointerType === "mouse" && later(true, OPEN_DELAY);
  const hoverOut = (e: PointerEvent) => e.pointerType === "mouse" && later(false, CLOSE_DELAY);
  // Havola/tugma ichida: bosish ota elementga yetmasin
  const stop = (e: MouseEvent | PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Anchor asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={t("aria")}
          aria-expanded={open}
          onPointerEnter={hoverIn}
          onPointerLeave={hoverOut}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            stop(e);
            window.clearTimeout(timer.current);
            setOpen(true);
          }}
          onFocus={() => later(true, 0)}
          onBlur={() => later(false, CLOSE_DELAY)}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setOpen((o) => !o);
            } else if (e.key === "Escape") setOpen(false);
          }}
          className={cn(
            "relative z-10 inline-grid shrink-0 cursor-help place-items-center rounded-full align-[-2px] text-faint transition-colors hover:text-ink focus-visible:text-ink focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:outline-none",
            className,
          )}
        >
          <Info size={size} strokeWidth={1.75} aria-hidden />
        </span>
      </Popover.Anchor>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerEnter={(e) => e.pointerType === "mouse" && window.clearTimeout(timer.current)}
          onPointerLeave={hoverOut}
          className="fade-in z-[80] max-w-[min(300px,calc(100vw-16px))] rounded-xl bg-ink px-3 py-2 text-[12px] leading-snug font-normal tracking-normal text-surface normal-case shadow-menu"
        >
          {text}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
